import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth.js";

/** mfaPending is treated the same as "no session" -- an aal1 session with a verified TOTP
 *  factor still outstanding isn't a completed sign-in. Login.tsx renders the code-entry
 *  step for that case using the same (already-established) session, so redirecting back to
 *  /login here doesn't lose any state.
 *
 *  needsPasswordSetup MUST be checked before mfaPending: an invite-accept/recovery link
 *  lands on a real (aal1) session, and for an account that already has TOTP enrolled from
 *  before, mfaPending is true on that session too. Checking mfaPending first bounced that
 *  session to /login for a code challenge before it ever reached /set-password, so the
 *  password was never actually set -- a real bug this order fixes (the recovery session
 *  gets used up on that detour, so simply retrying just repeats it). Setting a password
 *  doesn't need aal2; the MFA challenge still happens on the very next normal sign-in. */
export function RequireAuth() {
  const { session, loading, mfaPending, needsPasswordSetup } = useAuth();
  const location = useLocation();
  if (loading) return <Centered>Loading...</Centered>;
  if (!session) return <Navigate to="/login" replace />;
  if (needsPasswordSetup) {
    if (location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
    return <Outlet />;
  }
  if (mfaPending) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/** Gate for /admin. This is a UX convenience only, not the real security boundary --
 *  every table an admin screen reads/writes is ALSO protected by RLS keyed off
 *  is_platform_admin() (docs section 14 rule 6), so a client can't get admin data even
 *  by bypassing this check in the browser. */
export function RequirePlatformAdmin() {
  const { isPlatformAdmin, loading } = useAuth();
  if (loading) return <Centered>Loading...</Centered>;
  if (!isPlatformAdmin) return <Navigate to="/app" replace />;
  return <Outlet />;
}

/** Gate for /app: must belong to at least one business. Licence status (whether that
 *  business's client has an active license) is checked inside AppShell, not here, since
 *  it needs a query, not just the membership list already in AuthState. */
export function RequireBusinessMember() {
  const { memberships, loading } = useAuth();
  if (loading) return <Centered>Loading...</Centered>;
  if (memberships.length === 0) return <Centered>Your account isn't linked to a business yet. Contact your administrator.</Centered>;
  return <Outlet />;
}

/** Gate for the pages a staff login (one staff member's own view) must not see. The database refuses them the data anyway;
 *  this just sends them to their own pages instead of an empty screen. */
export function RequireManager() {
  const { memberships, activeBusinessId, isPlatformAdmin, loading } = useAuth();
  if (loading) return <Centered>Loading...</Centered>;
  const role = memberships.find((m) => m.business_id === activeBusinessId)?.role;
  if (role === "staff" && !isPlatformAdmin) return <Navigate to="/app/bookings" replace />;
  return <Outlet />;
}

/** Gate for the owner-only pages (Receptionist rules, Business settings, Invoices). A manager is sent back to the dashboard.
 *  The menu hides these too; this stops a typed or bookmarked address. */
export function RequireOwner() {
  const { memberships, activeBusinessId, isPlatformAdmin, loading } = useAuth();
  if (loading) return <Centered>Loading...</Centered>;
  const role = memberships.find((m) => m.business_id === activeBusinessId)?.role;
  if (role !== "owner" && !isPlatformAdmin) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 40, color: "var(--rc-text-muted)" }}>{children}</div>;
}
